import React from "react";
import { AcaIcon } from "../shared/aca";
import { VELLUM as T, SANS, MONO, mono, btn } from "../shared/folio/kit";

/* The folio vocabulary under the token names these files already used. */
const pal = {
  paper: T.ground,
  paperDeep: T.ground,
  card: T.ground,
  ink: T.ink,
  inkSoft: T.mute,
  inkMute: T.mute,
  rule: T.rule,
  ruleSoft: T.rule,
  accent: T.accent,
  onAccent: T.onAccent,
};
const fam = { sans: SANS, mono: MONO, serif: SANS };

interface JobDetailHeaderProps {
  title: string;
  institution: string;
  institutionType?: string;
  logoUrl?: string;
  isHot?: boolean;
  isSaved: boolean;
  hasApplied: boolean;
  showApply?: boolean;
  isOwner?: boolean;
  applicationsClosed?: boolean;
  /* The two figures a scholar decides on. They belong to the act, so they
     are set beside the button rather than filed with the particulars. */
  salary?: string;
  closesLabel?: string | null;
  daysLeft?: number | null;
  postedLabel?: string | null;
  /* Where / how / how long - the three facts that qualify a post before
     any of the prose is read. */
  spine?: string[];
  onSaveToggle: () => void;
  onShare: () => void;
  onApply: () => void;
  className?: string;
}

/* A post is either open to you, already answered, shut, or yours. Every
   part of the action block reads from this one word rather than each
   re-deriving the state from four booleans. */
type PostState = "owner" | "observer" | "applied" | "closed" | "open";

const STATE_WORD: Record<PostState, string> = {
  owner: "Your listing",
  observer: "Listing",
  applied: "On file",
  closed: "Closed",
  open: "Open",
};

const labelStyle: React.CSSProperties = {
  ...mono(T),
  fontSize: 10,
  letterSpacing: "0.2em",
  color: pal.inkSoft,
};

export const JobDetailHeader: React.FC<JobDetailHeaderProps> = ({
  title,
  institution,
  institutionType,
  logoUrl,
  isHot,
  isSaved,
  hasApplied,
  showApply = true,
  isOwner = false,
  applicationsClosed = false,
  salary,
  closesLabel,
  daysLeft,
  postedLabel,
  spine = [],
  onSaveToggle,
  onShare,
  onApply,
}) => {
  const hasValidLogo =
    logoUrl &&
    !logoUrl.includes("placeholder") &&
    !logoUrl.includes("via.placeholder");

  const state: PostState = isOwner
    ? "owner"
    : !showApply
    ? "observer"
    : applicationsClosed
    ? "closed"
    : hasApplied
    ? "applied"
    : "open";

  /* A fortnight is the point at which a closing stops being a date and
     starts being a deadline, so that is where the register raises its
     voice. Anything longer is set in the same ink as everything else. */
  const urgent = typeof daysLeft === "number" && daysLeft >= 0 && daysLeft <= 14;
  const closingNote =
    typeof daysLeft !== "number" || applicationsClosed
      ? null
      : daysLeft < 0
      ? null
      : daysLeft === 0
      ? "Closes today"
      : daysLeft === 1
      ? "1 day left"
      : `${daysLeft} days left`;

  return (
    <header>
      <div
        className="jd-masthead"
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) 348px",
          gap: 56,
          alignItems: "start",
        }}
      >
        {/* Identity column */}
        <div>
          {/* Eyebrow */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              marginBottom: 18,
            }}
          >
            <span style={{ ...mono(T, T.accent), letterSpacing: "0.22em" }}>
              § Post
            </span>
            {isHot && (
              <span
                style={{
                  ...mono(T, T.accent),
                  fontSize: 9,
                  letterSpacing: "0.22em",
                  border: `1px solid ${pal.accent}`,
                  padding: "3px 8px",
                }}
              >
                Hot position
              </span>
            )}
          </div>

          <h1
            style={{
              fontFamily: fam.sans,
              fontSize: "clamp(34px, 4.6vw, 56px)",
              lineHeight: 0.98,
              letterSpacing: "-0.03em",
              fontWeight: 600,
              margin: "0 0 26px",
              color: pal.ink,
              maxWidth: 720,
            }}
          >
            {title}
            <span style={{ color: pal.accent }}>.</span>
          </h1>

          {/* Institution identity */}
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            {hasValidLogo ? (
              <img
                src={logoUrl}
                alt=""
                style={{
                  width: 44,
                  height: 44,
                  objectFit: "cover",
                  border: `1px solid ${pal.rule}`,
                  flexShrink: 0,
                }}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : (
              <div
                aria-hidden
                style={{
                  width: 44,
                  height: 44,
                  border: `1px solid ${pal.rule}`,
                  background: pal.paper,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <AcaIcon name="book" size={19} stroke={pal.inkSoft} />
              </div>
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ ...labelStyle, marginBottom: 3 }}>
                {institutionType || "Institution"}
              </div>
              <div
                style={{
                  fontFamily: fam.sans,
                  fontSize: 18,
                  fontWeight: 600,
                  letterSpacing: "-0.01em",
                  color: pal.ink,
                }}
              >
                {institution}
              </div>
            </div>
          </div>

          {/* The spine: where, how, how long - ruled apart, never repeated
              in the particulars below. */}
          {spine.length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                marginTop: 22,
                borderTop: `1px solid ${pal.rule}`,
                paddingTop: 14,
              }}
            >
              {spine.map((fact, i) => (
                <span
                  key={fact}
                  style={{
                    ...mono(T, T.ink),
                    fontSize: 10.5,
                    letterSpacing: "0.16em",
                    padding: i === 0 ? "2px 16px 2px 0" : "2px 16px",
                    borderLeft: i === 0 ? "none" : `1px solid ${pal.rule}`,
                  }}
                >
                  {fact}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* The action block - the sheet's title block. Everything a scholar
            needs in order to decide, and the decision itself, inside one
            heavy border so the page has exactly one place to act. */}
        <div
          className="jd-action"
          style={{
            border: `1.5px solid ${pal.ink}`,
            background: pal.paper,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "11px 16px",
              borderBottom: `1px solid ${pal.rule}`,
            }}
          >
            <span style={mono(T, T.accent)}>§ The post</span>
            <span
              style={{
                ...mono(T),
                color: state === "closed" ? pal.accent : pal.inkSoft,
              }}
            >
              {STATE_WORD[state]}
            </span>
          </div>

          {/* Figures */}
          <dl style={{ margin: 0 }}>
            <div style={{ padding: "15px 16px" }}>
              <dt style={{ ...labelStyle, marginBottom: 6 }}>Salary</dt>
              <dd
                style={{
                  margin: 0,
                  fontFamily: fam.sans,
                  fontSize: 21,
                  fontWeight: 600,
                  letterSpacing: "-0.015em",
                  lineHeight: 1.2,
                  color: salary ? pal.ink : pal.inkSoft,
                }}
              >
                {salary || "Not disclosed"}
              </dd>
            </div>
            <div
              style={{
                padding: "15px 16px",
                borderTop: `1px solid ${pal.rule}`,
              }}
            >
              <dt style={{ ...labelStyle, marginBottom: 6 }}>Closes</dt>
              <dd
                style={{
                  margin: 0,
                  display: "flex",
                  alignItems: "baseline",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    fontFamily: MONO,
                    fontSize: 15,
                    color: applicationsClosed ? pal.accent : pal.ink,
                  }}
                >
                  {applicationsClosed
                    ? "Applications closed"
                    : closesLabel || "No closing date"}
                </span>
                {closingNote && (
                  <span
                    style={{
                      ...mono(T, urgent ? T.accent : T.mute),
                      fontSize: 9.5,
                      letterSpacing: "0.16em",
                    }}
                  >
                    {closingNote}
                  </span>
                )}
              </dd>
              {postedLabel && (
                <div
                  style={{
                    ...mono(T),
                    fontSize: 9.5,
                    letterSpacing: "0.16em",
                    color: pal.inkMute,
                    marginTop: 8,
                  }}
                >
                  Posted {postedLabel}
                </div>
              )}
            </div>
          </dl>

          {/* The act */}
          <div
            style={{
              padding: "16px",
              borderTop: `1px solid ${pal.rule}`,
            }}
          >
            {state === "open" ? (
              <>
                <button
                  type="button"
                  onClick={onApply}
                  className="jd-accent"
                  style={{
                    ...btn(T, "accent"),
                    width: "100%",
                    justifyContent: "center",
                  }}
                >
                  Apply for this position
                </button>
                <div
                  style={{
                    ...mono(T),
                    fontSize: 9.5,
                    letterSpacing: "0.16em",
                    color: pal.inkMute,
                    marginTop: 10,
                    textAlign: "center",
                  }}
                >
                  Attach a CV to apply
                </div>
              </>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  ...mono(T),
                  fontSize: 10,
                  color: state === "closed" ? pal.accent : pal.inkSoft,
                  border: `1px solid ${state === "closed" ? pal.accent : pal.rule}`,
                  padding: "13px 14px",
                  justifyContent: "center",
                }}
              >
                <AcaIcon
                  name={state === "applied" ? "check" : "doc"}
                  size={13}
                  stroke={state === "closed" ? pal.accent : pal.inkSoft}
                />
                {state === "applied"
                  ? "Application on file"
                  : state === "owner"
                  ? "You posted this position"
                  : state === "closed"
                  ? "Applications have closed"
                  : "Open to scholars only"}
              </div>
            )}
          </div>

          {/* Save / share - two half cells, ruled apart. */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              borderTop: `1px solid ${pal.rule}`,
            }}
          >
            <button
              type="button"
              onClick={onSaveToggle}
              aria-pressed={isSaved}
              aria-label={isSaved ? "Remove this post from saved" : "Save this post"}
              className="jd-cell-btn"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 7,
                background: "transparent",
                border: "none",
                borderRight: `1px solid ${pal.rule}`,
                padding: "13px 10px",
                cursor: "pointer",
                ...mono(T),
                fontSize: 10,
                color: isSaved ? pal.accent : pal.inkSoft,
              }}
            >
              <AcaIcon
                name="bookmark"
                size={13}
                stroke={isSaved ? pal.accent : pal.inkSoft}
              />
              {isSaved ? "Saved" : "Save"}
            </button>
            <button
              type="button"
              onClick={onShare}
              aria-label="Share this post"
              className="jd-cell-btn"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 7,
                background: "transparent",
                border: "none",
                padding: "13px 10px",
                cursor: "pointer",
                ...mono(T),
                fontSize: 10,
                color: pal.inkSoft,
              }}
            >
              <AcaIcon name="arrow" size={13} stroke={pal.inkSoft} />
              Share
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};
